// ServiceCreditPanel — where service credits are recorded, on the KPI tab
// beside the results that justify them.
//
// A month is offered when it carries at least one breached or at-risk result,
// OR when a credit has already been recorded against it. The second half
// matters: without it, a credit entered in error against a month whose
// results are later corrected to green, or removed entirely, would have no
// correction path and a wrong monetary figure would stand for good.
//
// Months come from the canonical placement the compliance headline uses, so a
// weekly result closing on 24 May is a May candidate here and a May column on
// the report — the two surfaces cannot disagree about which month a failure
// belongs to.
//
// Recorded credits are read from the KPI report at the contract-to-date
// range, which is the only read that carries every month's credit. The report
// renders the same rows read-only; recording happens here.

import { useMemo, useState } from "react";
import Button from "../../../../common/Button";
import LoadingSpinner from "../../../../common/LoadingSpinner";
import Alert from "../../../../common/Alert";
import ServiceCreditModal from "./ServiceCreditModal";
import { useAuth } from "../../../../../contexts/AuthContext";
import { useReportKpi } from "../../../../../hooks/useContractReport";
import { placeResultsOnMonths } from "../../../../../lib/kpi/monthCompliance";
import { formatAud2dp } from "../../../../../utils/formatCurrency";
import { formatMonthYear } from "../../../../../utils/formatShortDate";
import type { Kpi, KpiReport } from "../../../../../types";
import type { ServiceCredit } from "../../../../../types/report";

interface CreditRow {
  month: string;
  credit: ServiceCredit | null;
}

export interface ServiceCreditPanelProps {
  contractId: string;
  kpis: Kpi[];
  reports: KpiReport[];
}

export default function ServiceCreditPanel({
  contractId,
  kpis,
  reports,
}: ServiceCreditPanelProps) {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "editor";

  // Contract-to-date: a credit recorded long before the window the reader is
  // looking at still has to be reachable for correction.
  const { data, isPending, error } = useReportKpi(contractId, "all");
  const [editingMonth, setEditingMonth] = useState<string | null>(null);

  const rows = useMemo<CreditRow[]>(() => {
    const credits = new Map(
      (data?.credits ?? []).map((credit) => [credit.month, credit]),
    );

    const failing = new Set<string>();
    for (const placed of placeResultsOnMonths(
      kpis.map((kpi) => ({
        kpiId: kpi.id,
        measurementFrequency: kpi.measurementFrequency,
      })),
      reports.map((report) => ({
        kpiId: report.parentId,
        period: report.period,
        rating: report.ragRating,
        recordedAt: report.updatedAt ?? report.createdAt ?? null,
      })),
    )) {
      if (placed.rating === "red" || placed.rating === "amber") {
        failing.add(placed.month);
      }
    }

    const months = new Set([...failing, ...credits.keys()]);
    return [...months]
      .sort((a, b) => b.localeCompare(a))
      .map((month) => ({ month, credit: credits.get(month) ?? null }));
  }, [data, kpis, reports]);

  if (isPending) {
    return (
      <div className="flex justify-center py-6">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert variant="error">
        Recorded service credits couldn&apos;t be loaded. Refresh to try again.
      </Alert>
    );
  }

  const editing = rows.find((row) => row.month === editingMonth) ?? null;

  return (
    <div
      data-testid="kpi-credits-panel"
      className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800/60"
    >
      <h3 className="text-h4 text-slate-900 dark:text-slate-100">
        Service credits
      </h3>
      <p className="mt-1 text-body-sm text-slate-500 dark:text-slate-400">
        Recorded per month, against the month&apos;s failures taken together.
        Amounts are never derived from a KPI&apos;s credit formula — that text
        is guidance, not a calculation.
      </p>

      {rows.length === 0 ? (
        <p
          data-testid="credit-none-due"
          className="mt-3 text-body-sm text-slate-500 dark:text-slate-400"
        >
          No month has a breached or at-risk result, and none carries a
          recorded credit.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((row) => (
            <li
              key={row.month}
              data-testid={`credit-month-${row.month}`}
              className="flex flex-wrap items-center justify-between gap-3 py-2.5"
            >
              <div className="min-w-0">
                <p className="text-body-sm font-medium text-slate-900 dark:text-slate-100">
                  {formatMonthYear(`${row.month}-01`)}
                </p>
                {/* A row with no credit is only ever here because the month
                    failed — a clean, uncredited month is not listed at all —
                    so there is one thing to say. */}
                <p className="text-caption text-slate-500 dark:text-slate-400">
                  {row.credit
                    ? row.credit.note
                    : "Breached or at-risk results this month"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <p className="text-body-sm tabular-nums text-slate-900 dark:text-slate-100">
                  {row.credit ? (
                    <>
                      {formatAud2dp(row.credit.amount)}{" "}
                      <span className="text-caption text-slate-500 dark:text-slate-400">
                        ex-GST
                        {row.credit.amount === 0 ? " · waived" : ""}
                      </span>
                    </>
                  ) : (
                    <span className="text-caption text-amber-700 dark:text-amber-400">
                      Not costed
                    </span>
                  )}
                </p>
                {/* The month is spoken but not repeated on screen: a column
                    of buttons all reading "Record credit" is unusable out of
                    visual context. The label opens with the visible run so it
                    still contains it. */}
                {canEdit && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setEditingMonth(row.month)}
                    aria-label={
                      row.credit
                        ? `Edit the service credit for ${formatMonthYear(`${row.month}-01`)}`
                        : `Record credit for ${formatMonthYear(`${row.month}-01`)}`
                    }
                  >
                    {row.credit ? "Edit" : "Record credit"}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <ServiceCreditModal
          isOpen
          onClose={() => setEditingMonth(null)}
          contractId={contractId}
          month={editing.month}
          credit={editing.credit}
        />
      )}
    </div>
  );
}
